import React, { useEffect, useState } from "react";
import { Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import styled from "@emotion/styled";
import { useDispatch } from "react-redux";
import { getAccounts } from "../redux/ducks/account";
import { getProjectMovePreview, moveProject } from "../redux/ducks/project";
import { showAlert } from "../redux/slices/alerts";
import { useHasPermission } from "../utils/permissionUtils";
import { tagDisplayName } from "./TagPicker";

const DialogContentStyled = styled(DialogContent)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    paddingTop: theme.spacing(1),
}));

const OptionBox = styled(Box)({
    display: "flex",
    flexDirection: "column",
});

const OptionName = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightSemiBold,
}));

const OptionClient = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.secondary,
}));

const PreviewLoading = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm,
}));

const FigureList = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
}));

const FigureRow = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(0.75, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

const FigureLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm,
}));

const FigureValue = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
    fontSize: theme.typography.size.sm,
    textAlign: "right",
}));

const BlockerLine = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    marginTop: theme.spacing(0.5),
}));

const TaskTagsSummary = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
}));

const ExpandButton = styled(Button)({
    minWidth: 0,
});

const TaskTagList = styled(FigureList)(({ theme }) => ({
    maxHeight: theme.spacing(40),
    overflowY: "auto",
    paddingLeft: theme.spacing(2),
}));

const MoveSpinner = styled(CircularProgress)({
    marginLeft: 8,
});

const formatCount = (value, singular, plural) => {
    const count = Number(value || 0);
    return `${count.toLocaleString("en-US")} ${count === 1 ? singular : plural}`;
};

const buildBlockerLines = (blockers) => {
    const lines = [];
    if (!blockers) return lines;
    const billedTimeRecords = Number(blockers.billedTimeRecords || 0);
    const billedCharges = Number(blockers.billedCharges || 0);
    const nonDuePayments = Number(blockers.nonDuePayments || 0);
    const groupPayments = Number(blockers.groupPayments || 0);
    if (billedTimeRecords > 0) {
        lines.push(`This project has ${formatCount(billedTimeRecords, "time record", "time records")} that ${billedTimeRecords === 1 ? "has" : "have"} already been billed.`);
    }
    if (billedCharges > 0) {
        lines.push(`It has ${formatCount(billedCharges, "charge", "charges")} that ${billedCharges === 1 ? "has" : "have"} already been billed.`);
    }
    if (nonDuePayments > 0) {
        lines.push(`It has ${formatCount(nonDuePayments, "payment", "payments")} that ${nonDuePayments === 1 ? "is" : "are"} no longer waiting to be paid.`);
    }
    if (groupPayments > 0) {
        lines.push(`Its group has ${formatCount(groupPayments, "payment", "payments")} that ${groupPayments === 1 ? "is" : "are"} no longer waiting to be paid.`);
    }
    return lines;
};

const MoveProjectDialog = ({ open, onClose, project, onMoved }) => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const canSeeInternal = hasPermission("canSeeInternal");
    const currentAccountId = project?.account?._id;

    const [accounts, setAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewFailed, setPreviewFailed] = useState(false);
    const [isMoving, setIsMoving] = useState(false);
    const [taskTagsExpanded, setTaskTagsExpanded] = useState(false);

    useEffect(() => {
        if (open) return;
        setSelectedAccount(null);
        setPreview(null);
        setPreviewLoading(false);
        setPreviewFailed(false);
        setIsMoving(false);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        setAccountsLoading(true);
        dispatch(getAccounts(0, 0, null, false, (data) => {
            const list = Array.isArray(data) ? data : [];
            setAccounts(list.filter((account) => account._id !== currentAccountId && account.active !== false));
            setAccountsLoading(false);
        }));
    }, [open, currentAccountId, dispatch]);

    useEffect(() => {
        if (!open || !project?._id || !selectedAccount?._id) {
            setPreview(null);
            setPreviewFailed(false);
            setPreviewLoading(false);
            return;
        }
        setPreview(null);
        setPreviewFailed(false);
        setTaskTagsExpanded(false);
        setPreviewLoading(true);
        dispatch(getProjectMovePreview(project._id, selectedAccount._id, (data) => {
            setPreviewLoading(false);
            if (!data) {
                setPreviewFailed(true);
                return;
            }
            setPreview(data);
        }));
    }, [open, project?._id, selectedAccount?._id, dispatch]);

    const handleClose = () => {
        if (isMoving) return;
        onClose();
    };

    const handleMove = () => {
        if (!project?._id || !selectedAccount?._id) return;
        setIsMoving(true);
        dispatch(moveProject(project._id, selectedAccount._id, (response) => {
            setIsMoving(false);
            if (!response) return;
            dispatch(showAlert({ severity: "success", message: response.message || `Project moved to ${selectedAccount.name}` }));
            onMoved?.(response);
            onClose();
        }));
    };

    const impact = preview?.impact;
    const tagList = (tags) => (tags || []).map((tag) => tagDisplayName(tag, canSeeInternal)).join(", ");
    const removedTags = tagList(impact?.tags?.removed);
    const addedTags = tagList(impact?.tags?.added);
    const taskTagChanges = (impact?.tags?.tasks || []).filter((task) => task?.removed?.length || task?.added?.length);
    const blockerLines = preview && preview.canMove === false ? buildBlockerLines(preview.blockers) : [];
    const canConfirm = !!preview?.canMove && !previewLoading && !isMoving;

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Move project to another account</DialogTitle>
            <DialogContentStyled>
                <Autocomplete
                    options={accounts}
                    loading={accountsLoading}
                    getOptionLabel={(o) => o.name || ""}
                    value={selectedAccount}
                    onChange={(_, v) => setSelectedAccount(v)}
                    isOptionEqualToValue={(o, v) => o._id === v._id}
                    renderOption={(props, option) => (
                        <li {...props} key={option._id}>
                            <OptionBox>
                                <OptionName>{option.name}</OptionName>
                                <OptionClient>{option.client?.name || "No client"}</OptionClient>
                            </OptionBox>
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Move to account" placeholder="Search accounts" />}
                    disabled={isMoving}
                    fullWidth
                />

                {previewLoading && (
                    <PreviewLoading>
                        <CircularProgress size={16} />
                        <span>Checking what this move would change...</span>
                    </PreviewLoading>
                )}

                {previewFailed && (
                    <Alert severity="error">We could not check this move right now. Try again in a moment, or pick a different account.</Alert>
                )}

                {preview && preview.canMove === false && (
                    <Alert severity="error">
                        <Typography variant="subtitle2">This project cannot be moved</Typography>
                        {blockerLines.map((line) => (
                            <BlockerLine key={line}>{line}</BlockerLine>
                        ))}
                        <BlockerLine>A project can only move while all of its work is still unbilled.</BlockerLine>
                    </Alert>
                )}

                {impact && (
                    <>
                        {preview?.canMove && impact.clientChanges && (
                            <Alert severity="warning">
                                <Typography variant="subtitle2">This also changes the client</Typography>
                                <BlockerLine>
                                    Everyone at {impact.toClient?.name} who can see the {impact.toAccount?.name} account will be able to read this project's
                                    existing comments and open its attachments.
                                </BlockerLine>
                            </Alert>
                        )}

                        <FigureList>
                            <FigureRow>
                                <FigureLabel>Project number</FigureLabel>
                                <FigureValue>
                                    #{impact.currentProjectId} becomes #{impact.nextProjectId}
                                </FigureValue>
                            </FigureRow>
                            <FigureRow>
                                <FigureLabel>Account</FigureLabel>
                                <FigureValue>
                                    {impact.fromAccount?.name} to {impact.toAccount?.name}
                                </FigureValue>
                            </FigureRow>
                            <FigureRow>
                                <FigureLabel>Client</FigureLabel>
                                <FigureValue>
                                    {impact.clientChanges ? `${impact.fromClient?.name} to ${impact.toClient?.name}` : `Stays ${impact.fromClient?.name}`}
                                </FigureValue>
                            </FigureRow>
                            <FigureRow>
                                <FigureLabel>Billing type</FigureLabel>
                                <FigureValue>
                                    {impact.currentBillingType === impact.billingTypeAfter
                                        ? `Stays ${impact.billingTypeAfter}`
                                        : `${impact.currentBillingType} becomes ${impact.billingTypeAfter}`}
                                </FigureValue>
                            </FigureRow>
                            <FigureRow>
                                <FigureLabel>Work moving with it</FigureLabel>
                                <FigureValue>
                                    {[
                                        formatCount(impact.tasks, "task", "tasks"),
                                        formatCount(impact.timeRecords, "time record", "time records"),
                                        formatCount(impact.attachments, "attachment", "attachments"),
                                    ].join(", ")}
                                </FigureValue>
                            </FigureRow>
                            {impact.cards > 0 && (
                                <FigureRow>
                                    <FigureLabel>Task board cards</FigureLabel>
                                    <FigureValue>{formatCount(impact.cards, "card", "cards")} move too</FigureValue>
                                </FigureRow>
                            )}
                            {removedTags && (
                                <FigureRow>
                                    <FigureLabel>Tags removed</FigureLabel>
                                    <FigureValue>{removedTags}</FigureValue>
                                </FigureRow>
                            )}
                            {addedTags && (
                                <FigureRow>
                                    <FigureLabel>Tags added</FigureLabel>
                                    <FigureValue>{addedTags}</FigureValue>
                                </FigureRow>
                            )}
                            {taskTagChanges.length > 0 && (
                                <FigureRow>
                                    <FigureLabel>Task tags</FigureLabel>
                                    <TaskTagsSummary>
                                        <FigureValue>Tags change on {formatCount(taskTagChanges.length, "task", "tasks")}</FigureValue>
                                        <ExpandButton size="small" onClick={() => setTaskTagsExpanded((expanded) => !expanded)} aria-expanded={taskTagsExpanded}>
                                            {taskTagsExpanded ? "Hide" : "Show"}
                                        </ExpandButton>
                                    </TaskTagsSummary>
                                </FigureRow>
                            )}
                            {taskTagsExpanded && (
                                <TaskTagList>
                                    {taskTagChanges.map((task) => (
                                        <FigureRow key={task._id}>
                                            <FigureLabel>Tags on “{task.name}”</FigureLabel>
                                            <FigureValue>
                                                {[task.removed?.length && `Removed: ${tagList(task.removed)}`, task.added?.length && `Added: ${tagList(task.added)}`].filter(Boolean).join(" · ")}
                                            </FigureValue>
                                        </FigureRow>
                                    ))}
                                </TaskTagList>
                            )}
                            {impact.groupWillBeCleared && (
                                <FigureRow>
                                    <FigureLabel>Group</FigureLabel>
                                    <FigureValue>{impact.groupName ? `Leaves "${impact.groupName}"` : "Leaves its group"}</FigureValue>
                                </FigureRow>
                            )}
                        </FigureList>
                    </>
                )}
            </DialogContentStyled>
            <DialogActions>
                <Button onClick={handleClose} disabled={isMoving}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleMove} disabled={!canConfirm}>
                    {isMoving ? (
                        <>
                            Moving... <MoveSpinner size={16} />
                        </>
                    ) : (
                        "Move project"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MoveProjectDialog;
